/* ==========================================================================
   Lumen — section-article.js
   <lumen-article> — progressive enhancements for the article page:
   1. Builds a "Contents" list from the H2 headings inside the article body
      (only when there are 3 or more), inserted after the intro (i.e. right
      before the first H2). Anchor links get slugified ids + smooth scroll.
   2. Wires the copy-link share button (navigator.clipboard + toast).
   Safe with no JS: the page works fully without this file.
   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('lumen-article')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function slugify(text) {
    var slug = String(text)
      .toLowerCase()
      .trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9À-ɏЀ-ӿ]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'section';
  }

  function uniqueId(base) {
    var id = base;
    var n = 2;
    while (document.getElementById(id)) {
      id = base + '-' + n;
      n += 1;
    }
    return id;
  }

  customElements.define(
    'lumen-article',
    class extends HTMLElement {
      connectedCallback() {
        this._buildToc();
        this._wireCopyLink();
      }

      _buildToc() {
        var body = this.querySelector('[data-article-body]');
        if (!body || body.dataset.toc === 'false') return;

        var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
        if (headings.length < 3) return;

        var nav = document.createElement('nav');
        nav.className = 'article__toc';

        var tocTitle = this.dataset.tocTitle || 'Contents';
        nav.setAttribute('aria-label', tocTitle);

        var title = document.createElement('p');
        title.className = 'article__toc-title';
        title.textContent = tocTitle;

        var list = document.createElement('ol');
        list.className = 'article__toc-list';

        headings.forEach(function (heading) {
          if (!heading.id) heading.id = uniqueId(slugify(heading.textContent));

          var item = document.createElement('li');
          item.className = 'article__toc-item';

          var link = document.createElement('a');
          link.className = 'article__toc-link';
          link.href = '#' + heading.id;
          link.textContent = heading.textContent.trim();

          item.appendChild(link);
          list.appendChild(item);
        });

        nav.appendChild(title);
        nav.appendChild(list);

        // "After the intro": everything before the first H2 stays above the TOC.
        body.insertBefore(nav, headings[0]);

        nav.addEventListener('click', function (event) {
          var link = event.target.closest('a[href^="#"]');
          if (!link) return;

          var target = document.getElementById(link.getAttribute('href').slice(1));
          if (!target) return;

          event.preventDefault();
          target.scrollIntoView({
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
            block: 'start'
          });
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', link.getAttribute('href'));
          }
          // Move focus for keyboard/screen-reader users without re-scrolling.
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      }

      _wireCopyLink() {
        var button = this.querySelector('[data-copy-link]');
        if (!button) return;

        var copyFallback = function (text) {
          var scratch = document.createElement('textarea');
          scratch.value = text;
          scratch.setAttribute('readonly', '');
          scratch.style.position = 'fixed';
          scratch.style.opacity = '0';
          document.body.appendChild(scratch);
          scratch.select();
          try {
            document.execCommand('copy');
          } catch (error) {
            /* no-op — worst case the toast still tells the user to try again */
          }
          scratch.remove();
        };

        button.addEventListener('click', function () {
          var url = button.dataset.copyUrl || window.location.href;
          var announce = function () {
            var message = button.dataset.copiedText || 'Link copied';
            if (window.theme && typeof window.theme.toast === 'function') {
              window.theme.toast(message);
            }
          };

          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
              .writeText(url)
              .then(announce)
              .catch(function () {
                copyFallback(url);
                announce();
              });
          } else {
            copyFallback(url);
            announce();
          }
        });
      }
    }
  );
})();
